import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import Footer from './Footer';
import { db } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import useSEO from '../hooks/useSEO';

function EducatorsPage({ logo }) {
  useSEO({
    title: 'Veydra for Educators',
    description: 'Bring simulation-driven systems thinking to your classroom. Flexible pricing and LMS integration for educators.'
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    educatorLevel: '',
    subjectAreas: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      subjectAreas: checked 
        ? [...prev.subjectAreas, value]
        : prev.subjectAreas.filter(area => area !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await addDoc(collection(db, 'form-submissions-incremental'), {
        ...formData,
        source: 'educators-page',
        page: '/educators',
        intent: 'education_pricing',
        createdAt: new Date(),
        lastUpdated: new Date()
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', educatorLevel: '', subjectAreas: [] });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const featuredSchools = [
    { name: 'Penn State', logo: '/pennstate-wef.png' },
    { name: 'System Dynamics Society', logo: '/isdc_system_dynamics_society.png' },
    { name: 'Decision Sciences Institute', logo: '/dsi.png' },
    { name: 'INFORMS', logo: '/informs2025.jpg' },
  ];

  const learningModules = [
    {
      title: 'Supply Chain Logistics Under Pressure',
      discipline: 'Supply Chain',
      description: 'See how small shifts in demand ripple into major supply chain disruptions.',
      previewLink: '/try-now'
    },
    {
      title: 'Predator-Prey Population Dynamics',
      discipline: 'Ecology / Systems',
      description: 'Explore classic Lotka-Volterra dynamics and ecosystem stability.',
      previewLink: '/try-now'
    },
    {
      title: 'Limits to Growth',
      discipline: 'Sustainability',
      description: 'Model resource constraints and exponential growth dynamics.',
      previewLink: '/try-now'
    },
    {
      title: 'Epidemic Spread Dynamics (SIR Model)',
      discipline: 'Public Health',
      description: 'Simulate how diseases spread through populations using compartmental models.',
      previewLink: '/try-now'
    },
    {
      title: 'Malaria Transmission Dynamics',
      discipline: 'Public Health / ABM',
      description: 'An agent-based hybrid model for understanding malaria transmission and control.',
      previewLink: '/try-now'
    },
    {
      title: 'Emergency Department Capacity',
      discipline: 'Healthcare',
      description: 'Balance patient flow, staffing levels, and wait times under pressure.',
      previewLink: '/try-now'
    }
  ];

  const academicEvents = [
    {
      date: 'March 19–21, 2026',
      title: 'ABSEL Conference — Logan, UT',
      description: 'Connecting with educators focused on experiential learning and business simulation.',
      link: 'https://absel.org/'
    },
    {
      date: 'May 16–19, 2026',
      title: 'IISE Annual Conference & Expo — Arlington, TX',
      description: 'Industry panel on the intersection of AI, simulation, and industrial engineering.',
      link: 'https://www.iise.org/annual/'
    },
    {
      date: 'February 18–20, 2026',
      title: 'SEDSI Conference — Jacksonville, FL',
      description: 'Presenting on "Interactive Simulations and Cutting-edge Innovations" in the DASI track.',
      link: 'https://sedsi.decisionsciences.org/conference/',
      past: true
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopNav logo={logo} />

      {/* Unified Hero */}
      <section id="for-educators" className="py-16 md:py-24 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-4xl mx-auto text-center">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
                Strategic Decision Lab
              </p>
              <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
                Bring systems thinking to life<br />in the classroom
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-3 max-w-3xl mx-auto">
                Teaching complex systems can't rely on lectures alone.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
                The Strategic Decision Lab gives faculty a turnkey way to teach systems thinking through browser-based simulations that make abstract concepts visible, interactive, and memorable.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-0 max-w-3xl mx-auto mb-8">
                <img
                  src="/preview-3.png"
                  alt="Students exploring system dynamics models"
                  className="w-full h-auto rounded border border-black"
                />
              </div>

              <p className="text-lg font-medium text-gray-900 mb-8">
                Students don't just study systems — they experience them.
              </p>

              <ul className="space-y-3 mb-8 text-gray-600 max-w-2xl mx-auto">
                <li className="flex items-start justify-center text-center">
                  <span className="text-gray-400 mr-3">—</span>
                  Ready-to-use lab modules and curriculum
                </li>
                <li className="flex items-start justify-center text-center">
                  <span className="text-gray-400 mr-3">—</span>
                  AI-assisted model building for students
                </li>
                <li className="flex items-start justify-center text-center">
                  <span className="text-gray-400 mr-3">—</span>
                  Classroom management and progress tracking
                </li>
                <li className="flex items-start justify-center text-center">
                  <span className="text-gray-400 mr-3">—</span>
                  Institutional pricing and support
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/try-now">
                  <button className="bg-gray-900 text-white px-8 py-4 rounded text-sm font-medium hover:bg-gray-800 transition-colors">
                    Preview LMS Integration →
                  </button>
                </a>
                <a href="#request-pricing">
                  <button className="border border-gray-300 text-gray-700 px-8 py-4 rounded text-sm font-medium hover:border-gray-400 hover:text-gray-900 transition-colors">
                    Request Licensing Info
                  </button>
                </a>
              </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 md:py-20 px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-4 tracking-tight text-center">
            Who this is for
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Designed for educators who want to make systems thinking tangible and applied.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-900 mb-2">Business Schools</h3>
              <p className="text-sm text-gray-600">MBA and undergraduate courses in operations, strategy, and supply chain.</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-900 mb-2">Public Policy Programs</h3>
              <p className="text-sm text-gray-600">MPA, MPP, and policy analysis courses exploring systemic effects.</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-900 mb-2">Systems Thinking</h3>
              <p className="text-sm text-gray-600">System dynamics, sustainability, and complexity science courses.</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-900 mb-2">Supply Chain & IE</h3>
              <p className="text-sm text-gray-600">Industrial engineering, logistics, and operations research programs.</p>
            </div>
          </div>

          {/* Trusted By */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider text-center mb-6">
              Trusted at
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {featuredSchools.map((school, index) => (
                <img 
                  key={index}
                  src={school.logo} 
                  alt={school.name} 
                  className="h-10 md:h-12 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 md:py-20 px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-4 tracking-tight text-center">
            What's included
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Everything you need to integrate simulation-based learning into your course.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">Curated Simulations</h3>
                  <p className="text-sm text-gray-600">Browser-based, no install or IT setup required. Students access instantly.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">Instructor Guide + Student Activities</h3>
                  <p className="text-sm text-gray-600">Ready-to-use lesson plans, discussion prompts, and assignments.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">AI-Enabled Learning Assistant</h3>
                  <p className="text-sm text-gray-600">Guided feedback and adaptive prompts help students learn by doing.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">Semester Pilot License</h3>
                  <p className="text-sm text-gray-600">For one course or section — perfect for trying it out.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
              <h3 className="font-medium text-gray-900 mb-4">Optional Add-Ons</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">+</span>
                  Multi-course or departmental licenses
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">+</span>
                  Faculty development workshops
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">+</span>
                  Custom academic content or co-developed curriculum modules
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">+</span>
                  Research collaboration opportunities
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Modules */}
      <section className="py-16 md:py-20 px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-4 tracking-tight text-center">
            Examples of Individual Learning Modules
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Ready-to-use simulations across disciplines. Each model has guided exploration towards learning objectives.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {learningModules.map((module, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-300 hover:shadow-sm transition-all group">
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {module.discipline}
                </span>
                <h3 className="font-medium text-gray-900 mt-4 mb-2 group-hover:text-gray-700">
                  {module.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">{module.description}</p>
                {module.previewLink && (
                  <Link 
                    to={module.previewLink}
                    className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700"
                  >
                    Preview this module
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Academic Events */}
      <section className="py-16 md:py-20 px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-4 tracking-tight text-center">
            Events where we connect with educators
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Upcoming and recent events where we're sharing research and connecting with the academic community.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {academicEvents.map((event, index) => (
              <a 
                key={index}
                href={event.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`border rounded-lg p-6 hover:border-gray-300 hover:shadow-sm transition-all group ${event.past ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-gray-500">{event.date}</span>
                  {event.past && (
                    <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Past</span>
                  )}
                </div>
                <h3 className="font-medium text-gray-900 mb-2 group-hover:text-gray-700">{event.title}</h3>
                <p className="text-sm text-gray-600">{event.description}</p>
              </a>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link to="/news" className="text-sm font-medium text-gray-900 hover:text-gray-700">
              See all events →
            </Link>
          </div>
        </div>
      </section>

      {/* How to Engage - Two Column with Form */}
      <section id="request-pricing" className="py-16 md:py-20 px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            {/* Left Column - Content */}
            <div>
              <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-6 tracking-tight">
                Ready to bring simulation to your classroom?
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Let's discuss pilot programs, departmental licensing, or custom curriculum development. We work with institutions of all sizes.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">No software to install</h3>
                    <p className="text-sm text-gray-600">Browser-based platform works on any device with internet access.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Ready-made curriculum</h3>
                    <p className="text-sm text-gray-600">Pre-built learning modules aligned to common course objectives.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Academic pricing</h3>
                    <p className="text-sm text-gray-600">Special rates for educational institutions and research teams.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Want to try it first?</p>
                <Link
                  to="/try-now"
                  className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700"
                >
                  Preview an LMS Integration
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>

                <p className="mt-4 text-sm text-gray-500 mb-2">Innovation in Experiential Learning:</p>
                <Link
                  to="/innovation/experience-to-alignment"
                  className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700"
                >
                  From Experience to Alignment: The System Dynamics Edge
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Request Education Pricing</h3>
              
              {submitStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Thank you!</h4>
                  <p className="text-gray-600">We'll send you education pricing details soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {submitStatus === 'error' && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      Something went wrong. Please try again.
                    </div>
                  )}
                  
                  {/* Full Name */}
                  <div className="mb-4">
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Full Name"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>

                  {/* Email */}
                  <div className="mb-4">
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div className="mb-4">
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Phone Number"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>

                  {/* Educator Level */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Educator Level</label>
                    <div className="space-y-2">
                      {['K-12', 'Undergraduate', 'Graduate', 'Adult Learning', 'Other'].map((level) => (
                        <label key={level} className="flex items-center cursor-pointer">
                          <input 
                            type="radio" 
                            name="educatorLevel"
                            value={level}
                            checked={formData.educatorLevel === level}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-gray-900 focus:ring-gray-900 border-gray-300"
                          />
                          <span className="ml-2 text-gray-700">{level}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Subject Areas */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Subject Areas</label>
                    <div className="space-y-2">
                      {[
                        { value: 'business_management', label: 'Business & Management' },
                        { value: 'public_policy', label: 'Public Policy & Administration' },
                        { value: 'analytics_modeling', label: 'Analytics & Modeling' },
                        { value: 'economics_econometrics', label: 'Economics' },
                        { value: 'systems_engineering', label: 'Systems & Engineering' },
                        { value: 'other', label: 'Other' }
                      ].map((subject) => (
                        <label key={subject.value} className="flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            value={subject.value}
                            checked={formData.subjectAreas.includes(subject.value)}
                            onChange={handleCheckboxChange}
                            className="w-4 h-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-gray-700">{subject.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get Pricing Details'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 px-8 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600">
            Questions? Reach us at{' '}
            <a href="mailto:hello@veydra.com" className="text-gray-900 font-medium hover:underline">
              hello@veydra.com
            </a>
            {' '}or{' '}
            <a href="tel:+18885434111" className="text-gray-900 font-medium hover:underline">
              +1 (888) 543-4111
            </a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default EducatorsPage;
